import { useLazyQuery } from '@apollo/client';
import { useEffect, useMemo } from 'react';
import { ClosedLostReport, ClosedLostReportInput } from '@trnsact/trnsact-shared-types';
import { useNotifications } from 'modules/notification';
import { dateRangeToMoments } from 'modules/finance/sharedFilters';
import { CLOSED_LOST_REPORT } from '../api';
import {
  ClosedLostFiltersState,
  ClosedLostReasonTotal,
  ClosedLostReportTotals,
  ClosedLostRow,
  ClosedLostSortKey,
  ClosedOutcomeSummary,
} from '../types';

export function useClosedLostReportData(
  filters: ClosedLostFiltersState,
  sorting: { key: ClosedLostSortKey; value: 'asc' | 'desc' }
) {
  const { showNotification } = useNotifications();
  const { key: sortKey, value: sortDir } = sorting;

  const [fetchReport, { data, loading }] = useLazyQuery<{ closedLostReport: ClosedLostReport }>(CLOSED_LOST_REPORT, {
    notifyOnNetworkStatusChange: true,
    onError: () => showNotification('Failed to load closed lost report data', { type: 'error' }),
  });

  const input = useMemo((): ClosedLostReportInput => {
    const { from, to } = dateRangeToMoments(filters.dateRange, filters.customDateRange);
    return {
      groupBy: filters.groupBy,
      filters: {
        date: {
          range: {
            from: from.toISOString(),
            to: to.toISOString(),
          },
          by: filters.dateType,
        },
        departmentId: filters.departmentId,
        ...(filters.submissionStage !== 'ALL' && { submissionStage: filters.submissionStage }),
        ...(filters.paymentType !== 'ALL' && { paymentType: filters.paymentType }),
      },
      sorting: {
        key: sortKey,
        value: sortDir,
      },
    };
  }, [filters, sortKey, sortDir]);

  useEffect(() => {
    // departmentId defaults asynchronously once department options load, so skip until resolved.
    if (!filters.departmentId) return;
    fetchReport({ variables: { input } });
  }, [input, fetchReport, filters.departmentId]);

  const rows = useMemo((): ClosedLostRow[] => data?.closedLostReport?.rows ?? [], [data]);

  const totals = useMemo(
    (): ClosedLostReportTotals => ({
      totalLostDeals: data?.closedLostReport?.totalLostDeals ?? 0,
      totalValueLost: data?.closedLostReport?.totalValueLost ?? 0,
      grossProfitLost: data?.closedLostReport?.grossProfitLost ?? 0,
      avgDaysToLost: data?.closedLostReport?.avgDaysToLost ?? 0,
      avgGrossProfitLost: data?.closedLostReport?.avgGrossProfitLost ?? 0,
      avgTotalValueLost: data?.closedLostReport?.avgTotalValueLost ?? 0,
    }),
    [data]
  );

  const reasonTotals = useMemo((): ClosedLostReasonTotal[] => data?.closedLostReport?.reasonTotals ?? [], [data]);

  const outcomeSummary = useMemo(
    (): ClosedOutcomeSummary => ({
      totalClosed: data?.closedLostReport?.outcomeSummary?.totalClosed ?? 0,
      closedWon: data?.closedLostReport?.outcomeSummary?.closedWon ?? 0,
      closedLost: data?.closedLostReport?.outcomeSummary?.closedLost ?? 0,
      percentWon: data?.closedLostReport?.outcomeSummary?.percentWon ?? 0,
      percentLost: data?.closedLostReport?.outcomeSummary?.percentLost ?? 0,
      valueWon: data?.closedLostReport?.outcomeSummary?.valueWon ?? 0,
      valueLost: data?.closedLostReport?.outcomeSummary?.valueLost ?? 0,
      wonMissingClosedOn: data?.closedLostReport?.outcomeSummary?.wonMissingClosedOn ?? 0,
    }),
    [data]
  );

  return { rows, totals, reasonTotals, outcomeSummary, loading, input };
}
